import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Binoculars, Map, Camera, Bird, Tent, Footprints, Trees, Compass } from 'lucide-react';

const WildlifeConservation: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Savanna Institute";
    
    return (
        <div className="min-h-screen bg-[#f4f1ea] font-sans text-[#3b3834] selection:bg-[#c97c5d] selection:text-white">
            {/* Texture Overlay */}
            <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-0" style={{ 
                backgroundImage: 'url("https://www.transparenttextures.com/patterns/cardboard.png")' 
            }}></div>

            {/* Navbar */}
            <nav className="relative z-50 flex justify-between items-center px-8 py-6 border-b border-[#dcd6c8] bg-[#f4f1ea]">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#c97c5d] rounded-full flex items-center justify-center text-[#f4f1ea]">
                        <Footprints size={20} />
                    </div>
                    <span className="font-bold text-lg tracking-wide uppercase text-[#3b3834]">{schoolName}</span>
                </div>

                <div className="hidden md:flex gap-8 text-xs font-bold uppercase tracking-widest text-[#8c857b]">
                    <a href="#" className="hover:text-[#c97c5d] transition-colors">Expeditions</a>
                    <a href="#" className="hover:text-[#c97c5d] transition-colors">Species</a>
                    <a href="#" className="hover:text-[#c97c5d] transition-colors">Research</a>
                    <a href="#" className="hover:text-[#c97c5d] transition-colors">Support</a>
                </div>

                <Button className="rounded-full bg-[#3b3834] text-[#f4f1ea] hover:bg-[#c97c5d] font-bold uppercase tracking-widest text-[10px] h-9 px-6 transition-colors">
                    Join Mission
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative py-20 px-8 lg:px-16 overflow-hidden">
                <div className="max-w-7xl mx-auto flex flex-col items-center text-center relative z-10">
                    <div className="mb-8">
                        <span className="inline-block border-b-2 border-[#c97c5d] pb-1 font-bold text-xs uppercase tracking-[0.2em] text-[#c97c5d]">
                            Global Conservation Initiative
                        </span>
                    </div>
                    
                    <h1 className="text-5xl md:text-8xl font-black text-[#3b3834] mb-8 leading-tight">
                        Protect <br/>
                        The <span className="text-[#c97c5d]">Untamed.</span>
                    </h1>
                    
                    <p className="font-medium text-[#6b665e] text-lg max-w-2xl mb-12 leading-relaxed">
                        Join the frontline of wildlife preservation. From the African savanna to the Amazon rainforest, our field programs empower you to make a difference.
                    </p>

                    {/* Image Grid Hero */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl h-[400px]">
                        <div className="rounded-2xl overflow-hidden relative group">
                            <img src="https://images.unsplash.com/photo-1517179043329-3765e94b0d0c?q=80&w=1974&auto=format&fit=crop" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Cheetah" />
                        </div>
                        <div className="rounded-2xl overflow-hidden relative group md:-mt-12 md:mb-12 shadow-xl">
                            <img src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?q=80&w=2071&auto=format&fit=crop" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Elephant" />
                            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-4 py-2 rounded-lg">
                                <span className="block text-[10px] font-bold uppercase tracking-widest text-[#3b3834]">New Project</span>
                                <span className="block text-sm font-bold text-[#c97c5d]">Elephant Corridor</span>
                            </div>
                        </div>
                        <div className="rounded-2xl overflow-hidden relative group">
                             <img src="https://images.unsplash.com/photo-1547969854-3e981971777b?q=80&w=1931&auto=format&fit=crop" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Parrot" />
                        </div>
                    </div>
                </div>
            </header>

            {/* Field Notes Section */}
            <section className="py-24 px-8 bg-[#ebe7df] border-t border-[#dcd6c8] relative z-20">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center gap-4 mb-16">
                        <div className="w-12 h-1 bg-[#c97c5d]"></div>
                        <h2 className="text-3xl font-bold text-[#3b3834]">Focus Areas</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {[
                            { icon: Binoculars, title: "Behavior", desc: "Observational studies in natural habitats." },
                            { icon: Map, title: "Tracking", desc: "GPS collaring and migration analysis." },
                            { icon: Camera, title: "Documentation", desc: "Photography and data collection." },
                            { icon: Trees, title: "Habitat", desc: "Reforestation and ecosystem recovery." },
                        ].map((item, i) => (
                            <div key={i} className="bg-[#f4f1ea] p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow hover:-translate-y-1 duration-300">
                                <item.icon className="text-[#c97c5d] mb-4" size={32} />
                                <h3 className="text-xl font-bold text-[#3b3834] mb-2">{item.title}</h3>
                                <p className="text-sm text-[#8c857b] font-medium leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#2b2926] text-[#f4f1ea] py-20 px-8">
                 <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                     <div>
                         <div className="flex items-center gap-3 mb-6">
                            <Compass size={24} className="text-[#c97c5d]" />
                            <span className="font-bold text-xl uppercase tracking-widest">{schoolName}</span>
                         </div>
                         <p className="text-sm text-[#8c857b] max-w-xs leading-relaxed">
                             Dedicated to the preservation of biodiversity through research, education, and community engagement.
                         </p>
                     </div>
                     
                     <div className="md:col-span-2 flex justify-between md:justify-end gap-16 font-bold text-xs uppercase tracking-widest text-[#8c857b]">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white">Field Guides</a></li>
                             <li><a href="#" className="hover:text-white">Volunteer</a></li>
                             <li><a href="#" className="hover:text-white">Donate</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white">Contact</a></li>
                             <li><a href="#" className="hover:text-white">Press</a></li>
                             <li><a href="#" className="hover:text-white">Policies</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-[#3b3834] text-center md:text-left text-[10px] text-[#6b665e] uppercase tracking-widest">
                     © 2026 {schoolName}. Keep it wild.
                 </div>
            </footer>
        </div>
    );
};

export default WildlifeConservation;
